import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";

/** Resolve a theme *preference* to the actual surface to paint.
 *  "system" follows the OS via prefers-color-scheme. */
export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  }
  return theme;
}

/** Paint the resolved theme onto <html data-theme> so tokens.css inverts.
 *  Always writes "light" | "dark" — never "system", which the CSS can't match. */
export function applyTheme(theme: Theme): void {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.theme = resolveTheme(theme);
  }
}

type UIState = {
  paletteOpen: boolean;
  sidebarCollapsed: boolean;
  // Mobile-only: whether the sidebar is currently slid in as a drawer.
  // Separate from sidebarCollapsed (which is a desktop preference) so the
  // two states don't trample each other across viewport changes.
  mobileNavOpen: boolean;
  theme: Theme;
  setPaletteOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setMobileNavOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      paletteOpen: false,
      sidebarCollapsed: false,
      mobileNavOpen: false,
      theme: "system",
      setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      // Single-icon toggle: flip to the explicit opposite of whatever is
      // currently painted. Starting from "system" this lands on an explicit
      // light/dark — the least surprising thing one icon can do.
      toggleTheme: () => {
        const next: Theme = resolveTheme(get().theme) === "dark" ? "light" : "dark";
        applyTheme(next);
        set({ theme: next });
      },
    }),
    {
      name: "m2s.ui",
      // mobileNavOpen is session-only by design — refresh should close the
      // drawer, not surprise you with it open on next visit.
      partialize: (s) => ({ sidebarCollapsed: s.sidebarCollapsed, theme: s.theme }),
    },
  ),
);
